"use client";

import { useState, useEffect } from "react";
import jsPDF from "jspdf";
import logo from "../assets/DesarrollandoTerritorios.png"
import { apiConfig } from "../config/api.config";

const authorizedEmails = [
  "comunicaciones@copacabana.gov.co",
  "alejandra.valbuena@copacabana.gov.co",
  "juridica@copacabana.gov.co",
  "dianasimpac@gmail.com",
];

const isAuthorizedUser = (email) => authorizedEmails.includes(email);

// ====================== Helpers generales ======================

const numeroATexto = (numero) => {
  const unidades = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
  const decenas = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
  const especiales = [
    "DIEZ",
    "ONCE",
    "DOCE",
    "TRECE",
    "CATORCE",
    "QUINCE",
    "DIECISÉIS",
    "DIECISIETE",
    "DIECIOCHO",
    "DIECINUEVE",
  ];
  const centenas = [
    "",
    "CIENTO",
    "DOSCIENTOS",
    "TRESCIENTOS",
    "CUATROCIENTOS",
    "QUINIENTOS",
    "SEISCIENTOS",
    "SETECIENTOS",
    "OCHOCIENTOS",
  ];

  const n = Number(numero || 0);
  if (!Number.isFinite(n)) return "CERO";
  if (n === 0) return "CERO";
  if (n === 100) return "CIEN";

  let numeroWork = Math.floor(n);
  let texto = "";

  if (numeroWork >= 1000000) {
    const millones = Math.floor(numeroWork / 1000000);
    if (millones === 1) texto += "UN MILLÓN ";
    else texto += numeroATexto(millones) + " MILLONES ";
    numeroWork %= 1000000;
  }

  if (numeroWork >= 1000) {
    const miles = Math.floor(numeroWork / 1000);
    if (miles === 1) texto += "MIL ";
    else texto += numeroATexto(miles) + " MIL ";
    numeroWork %= 1000;
  }

  if (numeroWork >= 100) {
    const cent = Math.floor(numeroWork / 100);
    texto += centenas[cent] + " ";
    numeroWork %= 100;
  }

  if (numeroWork >= 20) {
    const dec = Math.floor(numeroWork / 10);
    texto += decenas[dec];
    numeroWork %= 10;
    if (numeroWork > 0) texto += " Y " + unidades[numeroWork];
  } else if (numeroWork >= 10) {
    texto += especiales[numeroWork - 10];
  } else if (numeroWork > 0) {
    texto += unidades[numeroWork];
  }

  return texto.trim();
};

const nombreMes = (numMes) => {
  const meses = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];
  return meses[Number.parseInt(numMes, 10) - 1];
};

// Devuelve { dias: number|null, texto: string }
const calcularTiempoNecesidadInfo = (inicioStr, finStr) => {
  if (!inicioStr || !finStr) return { dias: null, texto: "" };
  const inicio = new Date(inicioStr);
  const fin = new Date(finStr);
  if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return { dias: null, texto: "" };
  if (fin < inicio) return { dias: null, texto: "" };
  const MS_POR_DIA = 1000 * 60 * 60 * 24;
  const dias = Math.floor((fin - inicio) / MS_POR_DIA) + 1;
  return { dias, texto: `${dias} días` };
};

// ====================== RUBROS: detección robusta ======================

const tryJsonParse = (v) => {
  if (typeof v !== "string") return v;
  const s = v.trim();
  if (!s) return v;
  const looksJson = (s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"));
  if (!looksJson) return v;
  try {
    return JSON.parse(s);
  } catch {
    return v;
  }
};

const normalizeRubrosGeneric = (raw) => {
  let v = tryJsonParse(raw);

  // Si viene como objeto {rubros: [...] } o {data: [...]} o {0:...,1:...}
  if (v && typeof v === "object" && !Array.isArray(v)) {
    if (Array.isArray(v.rubros)) v = v.rubros;
    else if (Array.isArray(v.rubrosData)) v = v.rubrosData;
    else if (Array.isArray(v.data)) v = v.data;
    else v = Object.values(v);
  }

  if (!Array.isArray(v)) return [];

  return v
    .map((r) => ({
      resumido: String(
        r.resumido ??
          r.discriminacion_rubros ??
          r.discriminacionRubros ??
          r.codigo ??
          r.cod ??
          r.rubro ??
          ""
      ).trim(),
      nombreRubro: String(
        r.nombreRubro ??
          r.nombre_rubro ??
          r.nombresRubros ??
          r.nombre ??
          r.descripcion ??
          r.nombreR ??
          ""
      ).trim(),
      valor: Number(r.valor ?? r.valores ?? r.valor_rubro ?? r.monto ?? r.valorNumerico ?? 0) || 0,
    }))
    .filter((r) => r.resumido || r.nombreRubro || r.valor);
};

const extractRubrosDeep = (obj) => {
  const visited = new Set();
  const hits = [];

  const walk = (node, path = "") => {
    if (node === null || node === undefined) return;

    // strings json
    if (typeof node === "string") {
      const parsed = tryJsonParse(node);
      if (parsed !== node) walk(parsed, path);
      return;
    }

    if (typeof node !== "object") return;
    if (visited.has(node)) return;
    visited.add(node);

    if (Array.isArray(node)) {
      if (node.length && typeof node[0] === "object") {
        const keys = Object.keys(node[0] || {});
        const looksLikeRubros =
          keys.some((k) => /rubro|rubros|discriminacion|resumido|codigo|cod/i.test(k)) &&
          keys.some((k) => /valor|valores|monto/i.test(k));
        if (looksLikeRubros) hits.push({ path, value: node });
      }
      node.forEach((it, idx) => walk(it, `${path}[${idx}]`));
      return;
    }

    for (const [k, v] of Object.entries(node)) {
      const p = path ? `${path}.${k}` : k;
      const vv = tryJsonParse(v);

      // ✅ Ampliado: detectar también discriminacionRubros / nombresRubros / valores
      if (/rubrosdata|rubros|rubro|rubros_data|rubrosjson|discriminacionrubros|nombresrubros|valores/i.test(k)) {
        hits.push({ path: p, value: vv });
      }

      walk(vv, p);
    }
  };

  walk(obj, "contratoFirmado");
  return hits;
};

// ✅ NORMALIZADOR FINAL (fallback) - ahora soporta tu formato real camelCase
const normalizeRubrosFromContrato = (contrato) => {
  if (!contrato) return [];

  // 0) rubrosData array/string
  if (contrato.rubrosData) {
    let raw = contrato.rubrosData;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
      } catch {
        raw = [];
      }
    }
    if (Array.isArray(raw)) {
      return raw
        .map((r) => ({
          resumido: String(r.resumido ?? r.discriminacion_rubros ?? r.discriminacionRubros ?? r.codigo ?? "").trim(),
          nombreRubro: String(r.nombreRubro ?? r.nombre_rubro ?? r.nombresRubros ?? r.nombre ?? "").trim(),
          valor: Number(r.valor ?? r.valores ?? 0) || 0,
        }))
        .filter((r) => r.resumido || r.nombreRubro || r.valor);
    }
  }

  // 1) ✅ TU CASO (camelCase separado):
  // discriminacionRubros: "01001,02002"
  // nombresRubros: "Rubro A||Rubro B"
  // valores: "1000||2000"
  if (contrato.discriminacionRubros && contrato.nombresRubros && contrato.valores) {
    const discriminacionArray = String(contrato.discriminacionRubros).split(",");
    const nombresArray = String(contrato.nombresRubros).split("||");
    const valoresArray = String(contrato.valores).split("||");

    return discriminacionArray.map((d, idx) => ({
      resumido: String(d || "").trim(),
      nombreRubro: nombresArray[idx] ? String(nombresArray[idx]).trim() : "",
      valor: valoresArray[idx] ? Number(String(valoresArray[idx]).trim() || 0) : 0,
    }));
  }

  // 2) snake_case separado (compat)
  if (contrato.discriminacion_rubros && contrato.nombre_rubro && contrato.valor) {
    const discriminacionArray = String(contrato.discriminacion_rubros).split(",");
    const nombresArray = String(contrato.nombre_rubro).split("||");
    const valoresArray = String(contrato.valor).split("||");

    return discriminacionArray.map((d, idx) => ({
      resumido: String(d || "").trim(),
      nombreRubro: nombresArray[idx] ? String(nombresArray[idx]).trim() : "",
      valor: valoresArray[idx] ? Number(String(valoresArray[idx]).trim() || 0) : 0,
    }));
  }

  // 3) array rubros
  if (Array.isArray(contrato.rubros)) {
    return contrato.rubros
      .map((r) => ({
        resumido: String(r.resumido ?? r.discriminacionRubros ?? r.discriminacion_rubros ?? r.codigo ?? "").trim(),
        nombreRubro: String(r.nombreRubro ?? r.nombresRubros ?? r.nombre_rubro ?? r.nombre ?? "").trim(),
        valor: Number(r.valor ?? r.valores ?? 0) || 0,
      }))
      .filter((x) => x.resumido || x.nombreRubro || x.valor);
  }

  // 4) si vienen sueltos (por si acaso): discriminacionRubros + valores sin nombres
  if (contrato.discriminacionRubros && contrato.valores) {
    const discriminacionArray = String(contrato.discriminacionRubros).split(",");
    const valoresArray = String(contrato.valores).split("||");
    return discriminacionArray.map((d, idx) => ({
      resumido: String(d || "").trim(),
      nombreRubro: "",
      valor: valoresArray[idx] ? Number(String(valoresArray[idx]).trim() || 0) : 0,
    }));
  }

  return [];
};

// ====================== Componente ======================

function RPGenerator({ contratoFirmado, onClose, user }) {
  const [formData, setFormData] = useState({
    fechaSolicitud: "",
    nombreBeneficiario: "",
    idBeneficiario: "",
    dependencia: "",
    objeto: "",
    numeroContrato: "",
    fechaContrato: "",
    numeroDisponibilidad: "",
    fechaInicioContrato: "",
    fechaFinContrato: "",
    tiempoNecesidad: "",
    tiempoNecesidadDias: null,
    rubros: [],
    centroCostos: "",
    componenteInversion: "",
    codigoCpc: "",
    linkSecop: "",
    valor: "",
    valorLetras: "",
    solicitante: "",
    firmaUrl: "",
    firmaAlcaldeUrl: contratoFirmado?.firma_alcalde_url || "",
    motivoRechazo: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contratoFirmado) return;

    try {
      console.log("🧩 RP contratoFirmado keys:", Object.keys(contratoFirmado || {}));
    } catch {}

    const presupuestoNumerico = Number(contratoFirmado.presupuestoTotal || 0) || 0;
    const presupuestoEnTexto = numeroATexto(presupuestoNumerico);

    const hits = extractRubrosDeep(contratoFirmado);
    console.log("🔎 RP rubros hits:", hits.map((h) => h.path));

    let rubrosFinal = [];
    for (const h of hits) {
      const normalized = normalizeRubrosGeneric(h.value);
      if (normalized.length) {
        rubrosFinal = normalized;
        console.log("✅ RP usando rubros desde:", h.path, normalized);
        break;
      }
    }

    if (!rubrosFinal.length) {
      const fallback = normalizeRubrosFromContrato(contratoFirmado);
      if (fallback.length) {
        rubrosFinal = fallback;
        console.log("✅ RP usando rubros fallback (normalizeRubrosFromContrato)", rubrosFinal);
      } else {
        console.warn("⚠️ RP: NO se encontraron rubros en contratoFirmado (ni deep, ni fallback).");
      }
    }

    const obtenerFechaLocal = () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    setFormData((prev) => ({
      ...prev,
      fechaSolicitud: obtenerFechaLocal(),
      dependencia: contratoFirmado.secretaria || "",
      objeto: contratoFirmado.objetoContractual || "",
      rubros: rubrosFinal,
      centroCostos: contratoFirmado.centroCostos || "",
      valor: presupuestoNumerico,
      valorLetras: presupuestoEnTexto,
      solicitante: contratoFirmado.nombreSolicitante || "",
      firmaUrl: contratoFirmado.firma_solicitante_url || "",
      firmaAlcaldeUrl: contratoFirmado.firma_alcalde_url || "",
    }));
  }, [contratoFirmado]);

  useEffect(() => {
    const info = calcularTiempoNecesidadInfo(formData.fechaInicioContrato, formData.fechaFinContrato);
    setFormData((prev) => ({
      ...prev,
      tiempoNecesidad: info.texto,
      tiempoNecesidadDias: info.dias,
    }));
  }, [formData.fechaInicioContrato, formData.fechaFinContrato]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const guardarRPEnBackend = async () => {
    if (!contratoFirmado || !contratoFirmado.id) {
      throw new Error("No hay contrato firmado para asociar el RP");
    }

    const rpData = {
      contract_id: contratoFirmado.id,
      fecha: formData.fechaSolicitud,
      nombreBeneficiario: formData.nombreBeneficiario,
      idBeneficiario: formData.idBeneficiario,
      dependencia: formData.dependencia,
      objetoIndicador: formData.objeto,
      numeroContrato: formData.numeroContrato,
      fechaContrato: formData.fechaContrato,
      numeroDisponibilidad: formData.numeroDisponibilidad,
      centroCostos: formData.centroCostos,
      componente: formData.componenteInversion || null,
      codigoCpc: formData.codigoCpc || null,
      linkSecop: formData.linkSecop || null,
      valorNumerico: String(formData.valor ?? ""),
      valorLetras: formData.valorLetras,
      solicitante: formData.solicitante,
      firmaUrl: formData.firmaUrl || null,
      firmaAlcaldeUrl: formData.firmaAlcaldeUrl || null,
      rubrosData: formData.rubros,
      motivoRechazo: formData.motivoRechazo || null,
      fechaInicioContrato: formData.fechaInicioContrato || null,
      fechaFinContrato: formData.fechaFinContrato || null,
      tiempoNecesidadDias: formData.tiempoNecesidadDias || null,
      tiempoNecesidad: formData.tiempoNecesidad || null,
    };

    const resp = await fetch(apiConfig.getUrl(apiConfig.endpoints.rpFormulariosCreate), {
  method: "POST",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
  credentials: "include",
  body: JSON.stringify(rpData),
});

    if (!resp.ok) {
      const txt = await resp.text().catch(() => "");
      throw new Error(txt || `Error HTTP ${resp.status}`);
    }

    const data = await resp.json().catch(() => null);
    console.log("✅ RP guardado exitosamente en backend:", data);
    return data;
  };

  const handleGenerarRP = async () => {
    setLoading(true);
    try {
      const camposRequeridos = [
        "fechaSolicitud",
        "nombreBeneficiario",
        "idBeneficiario",
        "dependencia",
        "objeto",
        "numeroContrato",
        "fechaContrato",
        "numeroDisponibilidad",
        "fechaInicioContrato",
        "fechaFinContrato",
        "tiempoNecesidad",
        "centroCostos",
        "componenteInversion",
        "linkSecop",
        "valor",
        "valorLetras",
        "solicitante",
      ];

      const camposFaltantes = camposRequeridos.filter((campo) => !String(formData[campo] ?? "").trim());
      if (camposFaltantes.length > 0) {
        alert(`Por favor complete los siguientes campos: ${camposFaltantes.join(", ")}`);
        setLoading(false);
        return;
      }

      const inicioContrato = new Date(formData.fechaInicioContrato);
      const finContrato = new Date(formData.fechaFinContrato);
      if (finContrato < inicioContrato) {
        alert("La fecha fin de contrato no puede ser anterior a la fecha inicio.");
        setLoading(false);
        return;
      }

      if (!formData.tiempoNecesidadDias) {
        alert("El campo 'Tiempo de la necesidad' es obligatorio y se calcula al elegir las fechas.");
        setLoading(false);
        return;
      }

      const [year, month, day] = formData.fechaSolicitud.split("-");
      const fechaFormateada = `${day} de ${nombreMes(month)} de ${year}`;

      console.log("Firma del solicitante:", formData.firmaUrl);
      console.log("Firma del alcalde:", formData.firmaAlcaldeUrl);
      console.log("Tiempo días enviado:", formData.tiempoNecesidadDias);

      await guardarRPEnBackend();

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      doc.setLineWidth(0.5);
      doc.rect(15, 15, 35, 25);

      try {
        doc.addImage(logoImage, "PNG", 17, 17, 31, 21);
      } catch (error) {
        console.warn("No se pudo cargar el escudo:", error);
      }

      doc.rect(50, 15, 115, 25);
      doc.rect(165, 15, 40, 25);

      doc.line(165, 22, 205, 22);
      doc.line(165, 29, 205, 29);
      doc.line(165, 36, 205, 36);

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ALCALDÍA DE COPACABANA", 107.5, 25, { align: "center" });
      doc.setFontSize(10);
      doc.text("SOLICITUD DE REGISTRO PRESUPUESTAL", 107.5, 32, { align: "center" });

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text("Código: A-CBS-F-011", 167, 20);
      doc.text("Versión: 10", 167, 27);
      doc.text("Fecha Actualización:", 167, 34);
      doc.text("13-01-2025", 167, 38);

      let currentY = 65;
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");

      const writeLabelValue = (label, value) => {
        doc.setFont("helvetica", "bold");
        doc.text(label, 15, currentY);
        const w = doc.getTextWidth(label) + 2;
        doc.setFont("helvetica", "normal");
        doc.text(String(value ?? ""), 15 + w, currentY);
        currentY += 8;
      };

      writeLabelValue("FECHA SOLICITUD:", fechaFormateada);
      writeLabelValue("NOMBRE BENEFICIARIO:", formData.nombreBeneficiario);
      writeLabelValue("ID BENEFICIARIO:", formData.idBeneficiario);
      writeLabelValue("DEPENDENCIA:", formData.dependencia);

      doc.setFont("helvetica", "bold");
      doc.text("OBJETO CON INDICADOR DEL PLAN DE DESARROLLO:", 15, currentY);
      currentY += 5;
      doc.setFont("helvetica", "normal");
      const objetoLines = doc.splitTextToSize(formData.objeto, 170);
      doc.text(objetoLines, 15, currentY);
      currentY += objetoLines.length * 5 + 5;

      writeLabelValue("N.° CONTRATO O RESOLUCIÓN:", formData.numeroContrato);

      const fechaContratoFormateada = new Date(formData.fechaContrato).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      writeLabelValue("FECHA DEL CONTRATO O RESOLUCIÓN:", fechaContratoFormateada);
      writeLabelValue("N.° DE DISPONIBILIDAD:", formData.numeroDisponibilidad);

      writeLabelValue("FECHA INICIO CONTRATO:", formData.fechaInicioContrato);
      writeLabelValue("FECHA FIN CONTRATO:", formData.fechaFinContrato);
      writeLabelValue("TIEMPO DE LA NECESIDAD:", formData.tiempoNecesidad);

      doc.setFont("helvetica", "bold");
      doc.text("RUBROS:", 15, currentY);
      currentY += 6;

      if (formData.rubros && formData.rubros.length > 0) {
        doc.setFontSize(10);
        doc.text("RESUMIDO", 15, currentY);
        doc.text("NOMBRE RUBRO", 50, currentY);
        doc.text("VALOR", 170, currentY, { align: "right" });
        currentY += 5;

        doc.setFont("helvetica", "normal");
        formData.rubros.forEach((rubroItem) => {
          doc.text(String(rubroItem.resumido || ""), 15, currentY);
          const nombreRubroLines = doc.splitTextToSize(String(rubroItem.nombreRubro || ""), 110);
          doc.text(nombreRubroLines, 50, currentY);
          doc.text(`$${Number(rubroItem.valor || 0).toLocaleString("es-CO")}`, 170, currentY, { align: "right" });
          currentY += Math.max(6, nombreRubroLines.length * 4);
        });

        currentY += 5;
      } else {
        doc.setFont("helvetica", "normal");
        doc.text("No se encontraron rubros para este contrato.", 15, currentY);
        currentY += 10;
      }

      writeLabelValue("CENTRO DE COSTOS:", formData.centroCostos);
      writeLabelValue("COMPONENTE DE INVERSIÓN:", formData.componenteInversion || "N/A");
      writeLabelValue("CÓDIGO CPC:", formData.codigoCpc || "N/A");
      writeLabelValue("LINK SECOP:", formData.linkSecop || "N/A");

      doc.setFont("helvetica", "bold");
      doc.text("VALOR:", 15, currentY);
      currentY += 5;
      doc.setFont("helvetica", "normal");
      doc.text(`${String(formData.valorLetras || "").toUpperCase()} PESOS`, 15, currentY);
      currentY += 5;
      doc.text(`ML $ ${Number(formData.valor || 0).toLocaleString("es-CO")}`, 15, currentY);
      currentY += 15;

      writeLabelValue("SOLICITANTE:", formData.solicitante);
      currentY += 5;

      doc.setFont("helvetica", "bold");
      doc.text("FIRMA SOLICITANTE:", 15, currentY);
      const anchoLabelFirmaSolicitante = doc.getTextWidth("FIRMA SOLICITANTE:") + 5;

      let firmasPendientes = 0;
      const verificarFinalizacion = () => {
        if (firmasPendientes === 0) finalizarPDF();
      };

      const cargarFirma = (url, x, y) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            doc.addImage(img, "PNG", x, y, 40, 15);
          } catch (e) {}
          firmasPendientes--;
          verificarFinalizacion();
        };
        img.onerror = () => {
          firmasPendientes--;
          verificarFinalizacion();
        };
        img.src = url;
      };

      if (formData.firmaUrl) {
        firmasPendientes++;
        cargarFirma(formData.firmaUrl, 15 + anchoLabelFirmaSolicitante, currentY - 10);
      }

      currentY += 20;

      if (user && isAuthorizedUser(user.email) && formData.firmaAlcaldeUrl) {
        doc.setFont("helvetica", "bold");
        doc.text("FIRMA ALCALDE:", 15, currentY);
        const anchoLabelFirmaAlcalde = doc.getTextWidth("FIRMA ALCALDE:") + 5;
        firmasPendientes++;
        cargarFirma(formData.firmaAlcaldeUrl, 15 + anchoLabelFirmaAlcalde, currentY - 10);
      }

      verificarFinalizacion();

      function finalizarPDF() {
        doc.setFontSize(8);
        doc.text("Página 1 de 1", 15, pageHeight - 10);
        doc.text("Código: A-CBS-F-011", pageWidth - 50, pageHeight - 10);

        const fileName = `RP_${contratoFirmado?.radicado || "nuevo"}_${new Date().toISOString().slice(0, 10)}.pdf`;
        doc.save(fileName);
        alert("RP generado, guardado y descargado exitosamente!");
        setLoading(false);
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Error al generar RP:", error);
      alert("Error al generar el RP: " + error.message);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "linear-gradient(to bottom, #1e3a8a, #1e40af)",
        padding: "1rem",
        boxSizing: "border-box",
      }}
    >
      <div className="container" style={{ maxWidth: "1000px", margin: "0 auto", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-header">
          <div style={{ padding: "5px", borderRadius: "4px", display: "inline-block", backgroundColor: "transparent" }}>
            <img src={logo || "/placeholder.svg"} alt="Logo" className="logo" />
          </div>
          <div>
            <h3>SOLICITUD DE</h3>
            <h3>REGISTRO PRESUPUESTAL</h3>
          </div>
          <div className="form-id">
            <span style={{ display: "block" }}>Código: A-CBS-F-011</span>
            <span style={{ display: "block" }}>Versión: 10</span>
            <span style={{ display: "block" }}>Fecha Actualización: 13-01-2025</span>
          </div>
        </div>

        <div className="form-content">
          <table className="form-table">
            <tbody>
              <tr>
                <th style={{ width: "200px" }}>
                  FECHA SOLICITUD: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="date"
                    value={formData.fechaSolicitud}
                    onChange={(e) => handleInputChange("fechaSolicitud", e.target.value)}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  NOMBRE BENEFICIARIO: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.nombreBeneficiario}
                    onChange={(e) => handleInputChange("nombreBeneficiario", e.target.value)}
                    className="form-control"
                    placeholder="Ingrese el nombre del beneficiario"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  ID BENEFICIARIO: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.idBeneficiario}
                    onChange={(e) => handleInputChange("idBeneficiario", e.target.value)}
                    className="form-control"
                    placeholder="Cédula o documento de identificación"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  DEPENDENCIA: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.dependencia}
                    onChange={(e) => handleInputChange("dependencia", e.target.value)}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  OBJETO CON INDICADOR DEL PLAN DE DESARROLLO: <span className="required">*</span>
                </th>
                <td>
                  <textarea
                    value={formData.objeto}
                    onChange={(e) => handleInputChange("objeto", e.target.value)}
                    rows={4}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  N.°CONTRATO O RESOLUCIÓN: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.numeroContrato}
                    onChange={(e) => handleInputChange("numeroContrato", e.target.value)}
                    className="form-control"
                    placeholder="Ej: COPA-08001-16-0004-2026"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  FECHA DEL CONTRATO O RESOLUCIÓN: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="date"
                    value={formData.fechaContrato}
                    onChange={(e) => handleInputChange("fechaContrato", e.target.value)}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  N.° DE DISPONIBILIDAD: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.numeroDisponibilidad}
                    onChange={(e) => handleInputChange("numeroDisponibilidad", e.target.value)}
                    className="form-control"
                    placeholder="Ej: 576-597"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  FECHA INICIO CONTRATO: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="date"
                    value={formData.fechaInicioContrato}
                    onChange={(e) => handleInputChange("fechaInicioContrato", e.target.value)}
                    className="form-control"
                    required
                    max={formData.fechaFinContrato || undefined}
                  />
                </td>
              </tr>

              <tr>
                <th>
                  FECHA FIN CONTRATO: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="date"
                    value={formData.fechaFinContrato}
                    onChange={(e) => handleInputChange("fechaFinContrato", e.target.value)}
                    className="form-control"
                    required
                    min={formData.fechaInicioContrato || undefined}
                  />
                </td>
              </tr>

              <tr>
                <th>
                  TIEMPO DE LA NECESIDAD (días): <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.tiempoNecesidad}
                    className="form-control"
                    readOnly
                    required
                    placeholder="Se calcula automáticamente al elegir las fechas"
                  />
                </td>
              </tr>

              <tr>
                <th>RUBROS:</th>
                <td colSpan={3}>
                  {formData.rubros && formData.rubros.length > 0 ? (
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>RESUMIDO</th>
                          <th>NOMBRE RUBRO</th>
                          <th>VALOR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formData.rubros.map((item, index) => (
                          <tr key={index}>
                            <td style={{ width: "15%" }}>
                              <input
                                type="text"
                                className="form-control"
                                value={item.resumido || ""}
                                readOnly
                                style={{ backgroundColor: "#f3f4f6" }}
                              />
                            </td>
                            <td style={{ width: "60%" }}>
                              <textarea
                                className="form-control"
                                rows={2}
                                value={item.nombreRubro || ""}
                                readOnly
                                style={{ backgroundColor: "#f3f4f6" }}
                              />
                            </td>
                            <td style={{ width: "25%" }}>
                              <input
                                type="text"
                                className="form-control"
                                value={`$${Number(item.valor || 0).toLocaleString("es-CO")}`}
                                readOnly
                                style={{ backgroundColor: "#f3f4f6" }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        color: "#6b7280",
                      }}
                    >
                      No hay rubros disponibles para este contrato
                    </div>
                  )}
                </td>
              </tr>

              <tr>
                <th>
                  CENTRO DE COSTOS: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.centroCostos}
                    onChange={(e) => handleInputChange("centroCostos", e.target.value)}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  COMPONENTE DE INVERSIÓN:<span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.componenteInversion}
                    onChange={(e) => handleInputChange("componenteInversion", e.target.value)}
                    className="form-control"
                    placeholder="Ingrese componente de inversión"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  LINK SECOP:<span className="required">*</span>
                </th>
                <td>
                  <input
                    type="url"
                    value={formData.linkSecop || ""}
                    onChange={(e) => handleInputChange("linkSecop", e.target.value)}
                    className="form-control"
                    placeholder="https://..."
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  VALOR: (En letras) <span className="required">*</span>
                </th>
                <td>
                  <textarea
                    value={formData.valorLetras}
                    onChange={(e) => handleInputChange("valorLetras", e.target.value)}
                    rows={2}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  VALOR: (Numérico) <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={Number(formData.valor || 0).toLocaleString("es-CO")}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[.,]/g, "");
                      handleInputChange("valor", numericValue);
                    }}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              <tr>
                <th>
                  SOLICITANTE: <span className="required">*</span>
                </th>
                <td>
                  <input
                    type="text"
                    value={formData.solicitante}
                    onChange={(e) => handleInputChange("solicitante", e.target.value)}
                    className="form-control"
                    required
                  />
                </td>
              </tr>

              {formData.firmaUrl && (
                <tr>
                  <th>FIRMA SOLICITANTE:</th>
                  <td>
                    <div className="firma-preview">
                      <img
                        src={formData.firmaUrl || "/placeholder.svg"}
                        alt="Firma del solicitante"
                        className="firma-image"
                      />
                    </div>
                  </td>
                </tr>
              )}

              {formData.firmaAlcaldeUrl && (
                <tr>
                  <th>FIRMA Alcalde:</th>
                  <td>
                    <div className="firma-preview">
                      <img
                        src={formData.firmaAlcaldeUrl || "/placeholder.svg"}
                        alt="Firma del alcalde"
                        className="firma-image"
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="btn btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button onClick={handleGenerarRP} className="btn btn-primary" disabled={loading}>
            {loading ? "Generando..." : "Generar y Guardar RP"}
          </button>
        </div>

        <div className="form-footer">
          <p>Página 1 de 1 • Oficina Jurídica</p>
        </div>
      </div>
    </div>
  );
}

export default RPGenerator;
